import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import './LakshmikantDashboard.css';

const LAKSHMIKANT_EMAIL = 'lakshmikant@siddharameshwar.com';

const formatDateTime = (value) => {
  if (!value) return '—';
  const str = String(value);
  if (str.length >= 10) {
    const [y, m, d] = str.slice(0, 10).split('-');
    if (y && m && d) {
      const timePart = str.length > 10 ? ` ${str.slice(11, 19)}` : '';
      return `${d}/${m}/${y}${timePart}`;
    }
  }
  return str;
};

const formatAmount = (value) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const DataTable = ({ columns, rows, emptyMessage = 'No records for selected date.' }) => (
  <div className="lk-table-wrap">
    <table className="lk-table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="lk-empty">
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row, idx) => (
            <tr key={row.id ?? `${idx}-${columns[0]?.key}`}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const Section = ({ title, count, children }) => (
  <section className="lk-section">
    <h3 className="lk-section-title">
      {title}
      <span className="lk-count">({count})</span>
    </h3>
    {children}
  </section>
);

const LakshmikantDashboard = ({ adminEmail }) => {
  const [selectedDate, setSelectedDate] = useState(getTodayYYYYMMDD());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const email = encodeURIComponent(adminEmail || LAKSHMIKANT_EMAIL);
      const qs = `date=${encodeURIComponent(selectedDate)}&admin_email=${email}`;
      const response = await fetch(`${API_BASE_URL}/api/admin/lakshmikant-dashboard?${qs}`);

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to load dashboard data.');
      }

      const payload = await response.json();
      setData(payload);
    } catch (err) {
      console.error('Lakshmikant dashboard fetch failed:', err);
      setError(err.message || 'Unable to load dashboard data.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [adminEmail, selectedDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const amountCol = (key, label) => ({
    key,
    label,
    render: (val) => formatAmount(val),
  });

  const dateCol = (key, label) => ({
    key,
    label,
    render: (val) => formatDateTime(val),
  });

  return (
    <div className="lakshmikant-dashboard">
      <div className="lk-header">
        <h2>Daily Transactions Dashboard</h2>
        <div className="lk-date-filters">
          <label>
            Date
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </label>
          <button type="button" className="lk-refresh-btn" onClick={fetchDashboard} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <p className="lk-error">{error}</p>}

      {data && (
        <>
          <div className="lk-category">
            <h2 className="lk-category-title">CD List</h2>
            <Section title="CD Commission Interest" count={data.cdList?.cdCommissionInterest?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('commission_interest_amount', 'Commission Interest'),
                  dateCol('created_at', 'Created At'),
                ]}
                rows={data.cdList?.cdCommissionInterest || []}
              />
            </Section>
            <Section title="CD EMI Payments" count={data.cdList?.cdEmiPayments?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('amount', 'Amount'),
                  dateCol('payment_date', 'Payment Date'),
                ]}
                rows={data.cdList?.cdEmiPayments || []}
              />
            </Section>
            <Section title="CD Loan" count={data.cdList?.cdLoan?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('loan_amount', 'Loan Amount'),
                  dateCol('date', 'Date'),
                ]}
                rows={data.cdList?.cdLoan || []}
              />
            </Section>
            <Section title="CD Overdue" count={data.cdList?.cdOverdue?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('overdue_paid', 'Overdue Paid'),
                  amountCol('md', 'MD'),
                  dateCol('payment_date', 'Payment Date'),
                ]}
                rows={data.cdList?.cdOverdue || []}
              />
            </Section>
          </div>

          <div className="lk-category">
            <h2 className="lk-category-title">GL List</h2>
            <Section title="EMI Payment" count={data.glList?.emiPayments?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  dateCol('payment_date', 'Payment Date'),
                  amountCol('amount', 'Amount'),
                ]}
                rows={data.glList?.emiPayments || []}
              />
            </Section>
            <Section title="General Loan" count={data.glList?.generalLoan?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  dateCol('date', 'Date'),
                  amountCol('loan_amount', 'Loan Amount'),
                  amountCol('commission_amount', 'Commission Amount'),
                ]}
                rows={data.glList?.generalLoan || []}
              />
            </Section>
            <Section title="Overdue Collected" count={data.glList?.overdueCollected?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('overdue_paid', 'Overdue Paid'),
                  amountCol('md', 'MD'),
                  dateCol('payment_date', 'Payment Date'),
                ]}
                rows={data.glList?.overdueCollected || []}
              />
            </Section>
          </div>

          <div className="lk-category">
            <h2 className="lk-category-title">TBD List</h2>
            <Section title="TBD Daily Payments" count={data.tbdList?.tbdDailyPayments?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('daily_amount', 'Daily Amount'),
                  dateCol('created_at', 'Created At'),
                ]}
                rows={data.tbdList?.tbdDailyPayments || []}
              />
            </Section>
            <Section title="TBD Loan" count={data.tbdList?.tbdLoan?.length || 0}>
              <DataTable
                columns={[
                  { key: 'account_no', label: 'Account No' },
                  amountCol('loan_amount', 'Loan Amount'),
                  amountCol('commission_amount', 'Commission Amount'),
                  dateCol('date', 'Date'),
                ]}
                rows={data.tbdList?.tbdLoan || []}
              />
            </Section>
          </div>

          <div className="lk-category">
            <h2 className="lk-category-title">RD List</h2>
            <Section title="FD Winner" count={data.rdList?.fdWinners?.length || 0}>
              <DataTable
                columns={[
                  { key: 'registration_no', label: 'Registration No' },
                  dateCol('created_at', 'Created At'),
                ]}
                rows={data.rdList?.fdWinners || []}
              />
            </Section>
            <Section title="FD Transactions" count={data.rdList?.fdTransactions?.length || 0}>
              <DataTable
                columns={[
                  { key: 'registration_no', label: 'Registration No' },
                  amountCol('amount', 'Amount'),
                  dateCol('created_at', 'Created At'),
                ]}
                rows={data.rdList?.fdTransactions || []}
              />
            </Section>
          </div>

          <div className="lk-category">
            <h2 className="lk-category-title">Pigmy List</h2>
            <Section title="Pigmy Payments" count={data.pigmyList?.pigmyPayments?.length || 0}>
              <DataTable
                columns={[
                  { key: 'registration_no', label: 'Registration No' },
                  amountCol('payment_amount', 'Payment Amount'),
                  dateCol('created_at', 'Created At'),
                ]}
                rows={data.pigmyList?.pigmyPayments || []}
              />
            </Section>
            <Section title="Pigmy Withdrawals" count={data.pigmyList?.pigmyWithdrawals?.length || 0}>
              <DataTable
                columns={[
                  { key: 'registration_no', label: 'Registration No' },
                  amountCol('withdrawal_amount', 'Withdrawal Amount'),
                  dateCol('withdrawal_date', 'Withdrawal Date'),
                ]}
                rows={data.pigmyList?.pigmyWithdrawals || []}
              />
            </Section>
          </div>

          <div className="lk-category">
            <h2 className="lk-category-title">Expenditure List</h2>
            <Section title="Expense Manager" count={data.expenditureList?.expenseManager?.length || 0}>
              <DataTable
                columns={[
                  { key: 'headerName', label: 'Header Name' },
                  amountCol('amount', 'Amount'),
                  dateCol('created_at', 'Created At'),
                ]}
                rows={data.expenditureList?.expenseManager || []}
              />
            </Section>
          </div>
        </>
      )}
    </div>
  );
};

export default LakshmikantDashboard;
export { LAKSHMIKANT_EMAIL };
